import { useEffect, useState } from "react";
import { Hook, Nullable } from "sonobello.utilities.react";

import { useObx } from "../../hooks/useObx";
import ApiRequestPaths from "../../constants/ApiRequestPaths";
import IBookingCenterOptionResponse, { ResponseBookingCenterOption } from "../../types/IBookingCenterOptionResponse";
import ICenter from "../../types/ICenter";

export interface UseGetBookingCenterOptionsProps {
  /** The patient's postal code to use as the origin for the center lookup. */
  patientPostalCode: Nullable<string>;
  /** The postal code of the center to which the patient is assigned to be used as a fallback lookup origin. */
  centerPostalCode: Nullable<string>;
  /** The id of the center to which the patient is assigned. */
  centerId: string;
}

/** A hook that obtains the list of centers in which the application can book appointments if a calendar can be resolved. */
const UseGetBookingCenterOptions: Hook<ICenter[], boolean, UseGetBookingCenterOptionsProps> = ({
  patientPostalCode,
  centerId,
  centerPostalCode
}) => {
  const postalCodeOrigin = patientPostalCode || centerPostalCode;
  if (!postalCodeOrigin) throw new Error("At least one of patientPostalCode or centerPostalCode must be provided.");
  const { res, err, loading, setReq } = useObx<IBookingCenterOptionResponse[]>("Get Bookable Centers");

  const [result, setResult] = useState<Nullable<ICenter[]>>(() => {
    setReq(
      r =>
        r && {
          ...r,
          url: ApiRequestPaths.getCentersV3(postalCodeOrigin, centerId)
        }
    );
    return null;
  });

  useEffect(() => {
    res && setResult(res.payload.map(c => new ResponseBookingCenterOption(c)));
  }, [res]);

  return { result, error: Boolean(err), isLoading: loading };
};

export default UseGetBookingCenterOptions;
